/**
 * The fixed vocabularies of the plot practice and worker records (migration 0079). Each list is
 * the database CHECK's values with their labels, so a dropdown can never offer a value the
 * database refuses.
 */
type Option = { id: string; label: string };

/** Silviculture activities - also the activities a worker's rate is agreed for. */
export const SILVICULTURE_ACTIVITIES: Option[] = [
  { id: 'site_preparation', label: 'Site preparation' }, { id: 'pitting', label: 'Pitting' },
  { id: 'planting', label: 'Planting' }, { id: 'gap_filling', label: 'Gap filling / replanting' },
  { id: 'weeding', label: 'Weeding' }, { id: 'pruning', label: 'Pruning' }, { id: 'thinning', label: 'Thinning' },
  { id: 'singling', label: 'Singling' }, { id: 'coppice_management', label: 'Coppice management' },
  { id: 'climber_cutting', label: 'Climber cutting' }, { id: 'mulching', label: 'Mulching' },
  { id: 'soil_working', label: 'Soil working' }, { id: 'watering', label: 'Watering' },
  { id: 'fire_line', label: 'Fire line' }, { id: 'fencing', label: 'Fencing' }, { id: 'nursery', label: 'Nursery work' },
  { id: 'felling', label: 'Felling / harvesting' }, { id: 'other', label: 'Other' },
];

export const WORK_ACTIVITIES: Option[] = [
  ...SILVICULTURE_ACTIVITIES.filter((entry) => entry.id !== 'other'),
  { id: 'spraying', label: 'Spraying' }, { id: 'fertilizer_application', label: 'Fertilizer application' },
  { id: 'loading_unloading', label: 'Loading / unloading' }, { id: 'transport', label: 'Transport' },
  { id: 'watch_ward', label: 'Watch and ward' }, { id: 'general_labour', label: 'General labour' },
  // Processing sites (0086): the same agreements and payments serve a sawmill or seasoning unit.
  { id: 'cross_cutting', label: 'Cross-cutting' }, { id: 'debarking', label: 'Debarking' }, { id: 'sawing', label: 'Sawing' },
  { id: 'edging_trimming', label: 'Edging / trimming' }, { id: 'grading', label: 'Grading' }, { id: 'stacking', label: 'Stacking' },
  { id: 'kiln_operation', label: 'Kiln operation' }, { id: 'air_seasoning', label: 'Air seasoning' },
  { id: 'chemical_treatment', label: 'Chemical treatment' }, { id: 'machine_operation', label: 'Machine operation' },
  { id: 'packing', label: 'Packing' }, { id: 'maintenance', label: 'Maintenance' }, { id: 'security', label: 'Security' },
  { id: 'office_work', label: 'Office work' }, { id: 'other', label: 'Other' },
];

export const DONE_BY: Option[] = [
  { id: 'farmer', label: 'Farmer' }, { id: 'worker', label: 'Worker' }, { id: 'contractor', label: 'Contractor' },
  { id: 'family_member', label: 'Family member' }, { id: 'other', label: 'Other' },
];

export const INPUT_CATEGORIES: Option[] = [
  { id: 'fertilizer', label: 'Fertilizer' }, { id: 'manure_compost', label: 'Manure / compost' },
  { id: 'pesticide', label: 'Pesticide' }, { id: 'insecticide', label: 'Insecticide' },
  { id: 'fungicide', label: 'Fungicide' }, { id: 'herbicide', label: 'Weedicide / herbicide' },
  { id: 'growth_regulator', label: 'Growth regulator' }, { id: 'soil_amendment', label: 'Soil amendment (lime, gypsum)' },
  { id: 'bio_stimulant', label: 'Bio-stimulant' }, { id: 'other', label: 'Other' },
];
export const INPUT_NATURES: Option[] = [
  { id: 'chemical', label: 'Chemical' }, { id: 'bio', label: 'Bio (biological)' }, { id: 'organic', label: 'Organic' },
];
export const INPUT_PURPOSES: Option[] = [
  { id: 'nutrition', label: 'Nutrition' }, { id: 'pest_control', label: 'Pest control' },
  { id: 'disease_control', label: 'Disease control' }, { id: 'weed_control', label: 'Weed control' },
  { id: 'growth_promotion', label: 'Growth promotion' }, { id: 'soil_health', label: 'Soil health' },
  { id: 'preventive', label: 'Preventive' }, { id: 'other', label: 'Other' },
];
export const APPLICATION_METHODS: Option[] = [
  { id: 'foliar_spray', label: 'Foliar spray' }, { id: 'soil_broadcast', label: 'Soil broadcast' }, { id: 'basal', label: 'Basal' },
  { id: 'drench', label: 'Drench' }, { id: 'drip_fertigation', label: 'Drip / fertigation' }, { id: 'injection', label: 'Injection' },
  { id: 'ring_application', label: 'Ring application' }, { id: 'seed_treatment', label: 'Seed / seedling treatment' }, { id: 'other', label: 'Other' },
];
export const RATE_UNITS: Option[] = [
  { id: 'kg_per_ha', label: 'kg / ha' }, { id: 'g_per_ha', label: 'g / ha' }, { id: 'l_per_ha', label: 'L / ha' },
  { id: 'ml_per_ha', label: 'ml / ha' }, { id: 'kg_per_acre', label: 'kg / acre' }, { id: 'l_per_acre', label: 'L / acre' },
  { id: 'g_per_litre', label: 'g / L of water' }, { id: 'ml_per_litre', label: 'ml / L of water' },
  { id: 'kg_per_plant', label: 'kg / plant' }, { id: 'g_per_plant', label: 'g / plant' }, { id: 'ml_per_plant', label: 'ml / plant' },
];

export const IRRIGATION_SOURCES: Option[] = [
  { id: 'rainfed', label: 'Rainfed (no irrigation)' }, { id: 'borewell', label: 'Borewell / tube well' },
  { id: 'open_well', label: 'Open well' }, { id: 'canal', label: 'Canal' }, { id: 'river_stream', label: 'River / stream' },
  { id: 'pond_tank', label: 'Pond / tank' }, { id: 'check_dam', label: 'Check dam' },
  { id: 'rainwater_harvesting', label: 'Rainwater harvesting' }, { id: 'piped_supply', label: 'Piped supply' }, { id: 'other', label: 'Other' },
];
export const IRRIGATION_METHODS: Option[] = [
  { id: 'flood', label: 'Flood' }, { id: 'furrow', label: 'Furrow' }, { id: 'basin', label: 'Basin / ring' },
  { id: 'drip', label: 'Drip' }, { id: 'sprinkler', label: 'Sprinkler' }, { id: 'manual', label: 'Manual (pot / hose)' }, { id: 'none', label: 'None' },
];
export const IRRIGATION_FREQUENCIES: Option[] = [
  { id: 'daily', label: 'Daily' }, { id: 'alternate_days', label: 'Alternate days' }, { id: 'twice_weekly', label: 'Twice a week' },
  { id: 'weekly', label: 'Weekly' }, { id: 'fortnightly', label: 'Fortnightly' }, { id: 'monthly', label: 'Monthly' },
  { id: 'seasonal', label: 'Seasonal (a few times a season)' }, { id: 'as_needed', label: 'As needed' }, { id: 'none', label: 'None' },
];
export const SEASONS: Option[] = [
  { id: 'all_year', label: 'All year' }, { id: 'summer', label: 'Summer' }, { id: 'monsoon', label: 'Monsoon' },
  { id: 'winter', label: 'Winter' }, { id: 'kharif', label: 'Kharif' }, { id: 'rabi', label: 'Rabi' }, { id: 'zaid', label: 'Zaid' },
];

export const SOIL_TEXTURES: Option[] = [
  { id: 'sandy', label: 'Sandy' }, { id: 'loamy_sand', label: 'Loamy sand' }, { id: 'sandy_loam', label: 'Sandy loam' },
  { id: 'loam', label: 'Loam' }, { id: 'silt_loam', label: 'Silt loam' }, { id: 'clay_loam', label: 'Clay loam' },
  { id: 'clay', label: 'Clay' }, { id: 'laterite', label: 'Laterite' }, { id: 'other', label: 'Other' },
];

export const HEALTH: Option[] = [{ id: 'good', label: 'Good' }, { id: 'fair', label: 'Fair' }, { id: 'poor', label: 'Poor' }];

export const ISSUE_TYPES: Option[] = [
  { id: 'pest', label: 'Pest' }, { id: 'disease', label: 'Disease' }, { id: 'weed', label: 'Weed' },
  { id: 'nutrient_deficiency', label: 'Nutrient deficiency' }, { id: 'animal_damage', label: 'Animal / cattle damage' },
  { id: 'fire', label: 'Fire' }, { id: 'storm', label: 'Storm / wind' }, { id: 'other', label: 'Other' },
];
export const PLANT_PARTS: Option[] = [
  { id: 'leaf', label: 'Leaf' }, { id: 'stem', label: 'Stem' }, { id: 'bark', label: 'Bark' }, { id: 'root', label: 'Root' },
  { id: 'shoot', label: 'Shoot' }, { id: 'fruit', label: 'Fruit / seed' }, { id: 'whole_plant', label: 'Whole plant' },
];
export const SEVERITIES: Option[] = [
  { id: 'low', label: 'Low' }, { id: 'moderate', label: 'Moderate' }, { id: 'high', label: 'High' }, { id: 'severe', label: 'Severe' },
];
export const PEST_ACTIONS: Option[] = [
  { id: 'none', label: 'None yet' }, { id: 'monitoring', label: 'Monitoring' }, { id: 'manual_removal', label: 'Manual removal' },
  { id: 'cultural_practice', label: 'Cultural practice' }, { id: 'biological_control', label: 'Biological control' },
  { id: 'chemical_control', label: 'Chemical control' }, { id: 'pruning_sanitation', label: 'Pruning / sanitation' }, { id: 'other', label: 'Other' },
];
export const PEST_STATUS: Option[] = [
  { id: 'open', label: 'Open' }, { id: 'under_control', label: 'Under control' }, { id: 'resolved', label: 'Resolved' },
];

export const PLOT_TYPES: Option[] = [
  { id: 'production', label: 'Production' }, { id: 'conservation_area', label: 'Conservation area' },
  { id: 'hcv_area', label: 'High Conservation Value (HCV) area' }, { id: 'mixed', label: 'Production with a conservation part' },
];
export const HCV_CATEGORIES: Option[] = [
  { id: 'hcv1', label: 'HCV 1 - Species diversity' }, { id: 'hcv2', label: 'HCV 2 - Landscape-level ecosystems' },
  { id: 'hcv3', label: 'HCV 3 - Rare ecosystems and habitats' }, { id: 'hcv4', label: 'HCV 4 - Critical ecosystem services' },
  { id: 'hcv5', label: 'HCV 5 - Community needs' }, { id: 'hcv6', label: 'HCV 6 - Cultural values' },
];
export const CONSERVATION_ASPECTS: Option[] = [
  { id: 'riparian_buffer', label: 'Riparian / stream buffer' }, { id: 'wildlife_corridor', label: 'Wildlife corridor' },
  { id: 'nesting_breeding_site', label: 'Nesting / breeding site' }, { id: 'rare_threatened_species', label: 'Rare or threatened species' },
  { id: 'natural_forest_remnant', label: 'Natural forest remnant' }, { id: 'water_source_protection', label: 'Water source protection' },
  { id: 'slope_erosion_control', label: 'Slope / erosion control' }, { id: 'sacred_cultural_site', label: 'Sacred or cultural site' },
  { id: 'natural_regeneration', label: 'Natural regeneration' }, { id: 'grassland_wetland', label: 'Grassland / wetland' }, { id: 'other', label: 'Other' },
];
export const FSC_CLAIMS: Option[] = [
  { id: 'none', label: 'No FSC claim' }, { id: 'fsc_100', label: 'FSC 100%' }, { id: 'controlled_wood', label: 'Controlled Wood (OVS)' },
];

export const WORKER_TYPES: Option[] = [
  { id: 'permanent', label: 'Permanent' }, { id: 'seasonal', label: 'Seasonal' }, { id: 'casual', label: 'Casual / daily' },
  { id: 'piece_rate', label: 'Piece rate' }, { id: 'contract', label: 'Contract (through contractor)' },
  { id: 'family_labour', label: 'Family labour' }, { id: 'migrant', label: 'Migrant' },
];
export const RATE_BASES: Option[] = [
  { id: 'per_day', label: 'Per day' }, { id: 'per_hour', label: 'Per hour' }, { id: 'per_tree', label: 'Per tree' },
  { id: 'per_kg', label: 'Per kg' }, { id: 'per_unit', label: 'Per unit (say which)' }, { id: 'lump_sum', label: 'Lump sum' },
];
export const QUANTITY_OF: Record<string, string> = {
  per_day: 'Days worked', per_hour: 'Hours worked', per_tree: 'Trees done', per_kg: 'Kg done', per_unit: 'Units done', lump_sum: 'Jobs (1)',
};
export const PAYMENT_MODES: Option[] = [
  { id: 'cash', label: 'Cash' }, { id: 'upi', label: 'UPI' }, { id: 'bank_transfer', label: 'Bank transfer' }, { id: 'cheque', label: 'Cheque' },
];

export function optionLabel(options: Option[], id?: string | null): string {
  if (!id) return '--';
  return options.find((option) => option.id === id)?.label ?? id.replace(/_/g, ' ');
}

/** Sample values offered while typing (owner: "add sample values in all relevant places"). */
export const SAMPLES = {
  inputProduct: ['Urea (46% N)', 'DAP (18:46:0)', 'SSP (16% P2O5)', 'MOP (60% K2O)', 'NPK 10:26:26', 'Vermicompost', 'Farmyard manure (FYM)',
    'Neem cake', 'Neem oil 1500 ppm', 'Trichoderma viride 1% WP', 'Pseudomonas fluorescens', 'Beauveria bassiana', 'Mancozeb 75% WP',
    'Copper oxychloride 50% WP', 'Chlorpyriphos 20% EC', 'Imidacloprid 17.8% SL', 'Glyphosate 41% SL', 'Agricultural lime', 'Gypsum'],
  activeIngredient: ['Azadirachtin 0.03%', 'Mancozeb 75%', 'Copper oxychloride 50%', 'Chlorpyriphos 20%', 'Imidacloprid 17.8%', 'Glyphosate 41%', 'Trichoderma viride 1%'],
  manufacturer: ['IFFCO', 'Coromandel International', 'UPL', 'Bayer CropScience', 'Tata Rallis', 'Dhanuka Agritech', 'Local cooperative'],
  pestDisease: ['Teak defoliator (Hyblaea puera)', 'Teak skeletoniser (Eutectona machaeralis)', 'Termites', 'Stem borer', 'Bark-eating caterpillar',
    'Leaf spot', 'Powdery mildew', 'Pink disease', 'Root rot', 'Die-back', 'Gall wasp (Leptocybe invasa)', 'Lantana camara', 'Parthenium', 'Cattle browsing'],
  laboratory: ['District Soil Testing Laboratory', 'Krishi Vigyan Kendra (KVK)', 'State Agricultural University laboratory', 'Mobile soil testing van', 'Private NABL laboratory'],
  silvicultureDetail: ['1st weeding', '2nd weeding', 'Ring weeding around saplings', 'Lower branches pruned to 3 m', 'Thinning to 2 x 3 m spacing', 'Casualty replacement'],
  activityDetail: ['Ring weeding around saplings', 'Pit digging 45 cm cube', 'Loading logs onto truck', 'Night watch'],
  unitLabel: ['bundle', 'pit', 'trip', 'log', 'sapling'],
  venue: ['Panchayat office', 'Community hall', 'Village school', 'Farmer\'s courtyard', 'Range office'],
  referenceNo: ['VC/2026-27/001', 'SC/2026-27/001'],
  screeningSource: ['D35 SRC-01 to SRC-28'],
  documentRef: ['D27', 'D37'],
  deduction: ['Advance recovered', 'Tools lost', 'Canteen'],
  bodyPart: ['Hand', 'Finger', 'Foot', 'Leg', 'Eye', 'Head', 'Back'],
  complaintSummary: ['Wages not paid on time', 'Boundary dispute with neighbour', 'Path blocked by fencing', 'Spray drift into neighbour\'s field'],
  escalatedTo: ['Certification body', 'Block Development Officer', 'Labour department', 'Range Forest Officer'],
  permitNo: ['TP/2026/00123'],
  vehicleNo: ['WB 23 A 1234'],
  certificate: ['FSC-C123456', 'SA-FM/COC-012345'],
};

// ---- Registers (0079 / 0080) ----------------------------------------------
export const PERSON_TYPES: Option[] = [
  { id: 'worker', label: 'Worker' }, { id: 'farmer', label: 'Farmer' }, { id: 'contractor_staff', label: 'Contractor staff' },
  { id: 'visitor', label: 'Visitor' }, { id: 'community_member', label: 'Community member' }, { id: 'other', label: 'Other' },
];
export const INCIDENT_TYPES: Option[] = [
  { id: 'injury', label: 'Injury' }, { id: 'near_miss', label: 'Near miss' }, { id: 'occupational_illness', label: 'Occupational illness' },
  { id: 'fatality', label: 'Fatality' }, { id: 'property_damage', label: 'Property damage' }, { id: 'fire', label: 'Fire' },
  { id: 'chemical_exposure', label: 'Chemical exposure' },
];
export const ACCIDENT_SEVERITY: Option[] = [
  { id: 'near_miss', label: 'Near miss (no injury)' }, { id: 'first_aid', label: 'First aid only' }, { id: 'medical_treatment', label: 'Medical treatment' },
  { id: 'lost_time', label: 'Lost time' }, { id: 'permanent_disability', label: 'Permanent disability' }, { id: 'fatality', label: 'Fatality' },
];
export const ACCIDENT_STATUS: Option[] = [
  { id: 'open', label: 'Open' }, { id: 'investigating', label: 'Investigating' }, { id: 'action_pending', label: 'Action pending' }, { id: 'closed', label: 'Closed' },
];
export const GRIEVANCE_CHANNELS: Option[] = [
  { id: 'in_person', label: 'In person' }, { id: 'phone', label: 'Phone' }, { id: 'whatsapp', label: 'WhatsApp' }, { id: 'sms', label: 'SMS' },
  { id: 'letter', label: 'Letter' }, { id: 'email', label: 'Email' }, { id: 'meeting', label: 'Meeting' }, { id: 'complaint_box', label: 'Complaint box' }, { id: 'other', label: 'Other' },
];
export const COMPLAINANT_TYPES: Option[] = [
  { id: 'farmer', label: 'Farmer' }, { id: 'worker', label: 'Worker' }, { id: 'stakeholder', label: 'Stakeholder' },
  { id: 'community', label: 'Community' }, { id: 'anonymous', label: 'Anonymous' }, { id: 'other', label: 'Other' },
];
export const GRIEVANCE_CATEGORIES: Option[] = [
  { id: 'wages_payment', label: 'Wages / payment' }, { id: 'working_conditions', label: 'Working conditions' },
  { id: 'health_safety', label: 'Health and safety' }, { id: 'harassment_discrimination', label: 'Harassment / discrimination' },
  { id: 'land_boundary', label: 'Land / boundary' }, { id: 'customary_rights', label: 'Customary rights' }, { id: 'environment', label: 'Environment' },
  { id: 'harvest_trade', label: 'Harvest / trade' }, { id: 'consent_data', label: 'Consent / personal data' }, { id: 'staff_conduct', label: 'Staff conduct' }, { id: 'other', label: 'Other' },
];
export const GRIEVANCE_STATUS: Option[] = [
  { id: 'received', label: 'Received' }, { id: 'acknowledged', label: 'Acknowledged' }, { id: 'investigating', label: 'Investigating' },
  { id: 'resolved', label: 'Resolved' }, { id: 'closed', label: 'Closed' }, { id: 'escalated', label: 'Escalated' },
];
export const CONSULTATION_LEVELS: Option[] = [
  { id: 'organisation', label: 'Organisation level' }, { id: 'village', label: 'Village level' }, { id: 'farmer', label: 'Farmer / plot level' },
  { id: 'site', label: 'Site level (sawmill, depot ...)' },
];
export const CONSULTATION_MODES: Option[] = [
  { id: 'in_person', label: 'In person' }, { id: 'phone', label: 'Phone' }, { id: 'online', label: 'Online' }, { id: 'public_notice', label: 'Public notice' },
];
export const CONSULTATION_PURPOSES: Option[] = [
  { id: 'fmu_planning', label: 'FMU / management planning' }, { id: 'hcv_screening', label: 'HCV screening' }, { id: 'harvest_plan', label: 'Harvest plan' },
  { id: 'chemical_use', label: 'Chemical use' }, { id: 'fpic', label: 'FPIC (free, prior and informed consent)' }, { id: 'social_impact', label: 'Social impact' },
  { id: 'grievance', label: 'Grievance' }, { id: 'boundary', label: 'Boundary' }, { id: 'customary_rights', label: 'Customary rights' },
  { id: 'general', label: 'General' }, { id: 'other', label: 'Other' },
];
export const CONSULTATION_STATUS: Option[] = [
  { id: 'planned', label: 'Planned' }, { id: 'held', label: 'Held' }, { id: 'minutes_shared', label: 'Minutes shared' },
  { id: 'closed', label: 'Closed' }, { id: 'cancelled', label: 'Cancelled' },
];
export const AUDIT_TYPES: Option[] = [
  { id: 'internal', label: 'Internal audit' }, { id: 'pre_assessment', label: 'Pre-assessment' }, { id: 'follow_up', label: 'Follow-up' }, { id: 'special', label: 'Special' },
];
export const SELECTION_METHODS: Option[] = [
  { id: 'random', label: 'Random' }, { id: 'risk_based', label: 'Risk based' }, { id: 'stratified', label: 'Stratified' },
  { id: 'all_fmus', label: 'All FMUs' }, { id: 'targeted', label: 'Targeted' },
];
export const AUDIT_STATUS: Option[] = [
  { id: 'planned', label: 'Planned' }, { id: 'in_progress', label: 'In progress' }, { id: 'completed', label: 'Completed' }, { id: 'closed', label: 'Closed' },
];
export const SELECTION_REASONS: Option[] = [
  { id: 'random_draw', label: 'Random draw' }, { id: 'high_risk', label: 'High risk' }, { id: 'new_member', label: 'New member' },
  { id: 'previous_nc', label: 'Previous non-conformity' }, { id: 'complaint', label: 'Complaint' }, { id: 'harvest_planned', label: 'Harvest planned' },
  { id: 'hcv_present', label: 'HCV present' }, { id: 'other', label: 'Other' },
];
export const RISK_LEVELS: Option[] = [{ id: 'low', label: 'Low' }, { id: 'medium', label: 'Medium' }, { id: 'high', label: 'High' }];
export const AUDIT_RESULTS: Option[] = [
  { id: 'not_audited', label: 'Not audited yet' }, { id: 'conforming', label: 'Conforming' }, { id: 'observation', label: 'Observation' },
  { id: 'minor_nc', label: 'Minor non-conformity' }, { id: 'major_nc', label: 'Major non-conformity' },
];
export const TRADE_TYPES: Option[] = [{ id: 'sale', label: 'Sale' }, { id: 'purchase', label: 'Purchase' }];
export const TRADE_PARTY_TYPES: Option[] = [
  { id: 'farmer', label: 'Farmer (member)' }, { id: 'organisation', label: 'Our organisation' }, { id: 'trader', label: 'Trader' },
  { id: 'processor', label: 'Processor / mill' }, { id: 'other', label: 'Other' },
];
export const TRADE_PRODUCTS: Option[] = [
  { id: 'logs', label: 'Logs' }, { id: 'poles', label: 'Poles' }, { id: 'pulpwood', label: 'Pulpwood' }, { id: 'fuelwood', label: 'Fuelwood' },
  { id: 'bamboo', label: 'Bamboo' }, { id: 'sawn_timber', label: 'Sawn timber' }, { id: 'chips', label: 'Chips' }, { id: 'seedlings', label: 'Seedlings' }, { id: 'other', label: 'Other' },
];
export const PAYMENT_STATUS: Option[] = [{ id: 'unpaid', label: 'Unpaid' }, { id: 'partly_paid', label: 'Partly paid' }, { id: 'paid', label: 'Paid' }];
export const TRADE_STATUS: Option[] = [{ id: 'draft', label: 'Draft' }, { id: 'confirmed', label: 'Confirmed' }, { id: 'cancelled', label: 'Cancelled' }];
export const YES_NO: Option[] = [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }];
export const YES_NO_NA: Option[] = [...YES_NO, { id: 'not_applicable', label: 'Not applicable' }];
export const YES_NO_NH: Option[] = [...YES_NO, { id: 'not_harvested', label: 'Not harvested in period' }];
/**
 * Where a batch of planting stock came from (owner, 2026-09-25: "Add a sapling issue record").
 *
 * Deliberately NOT `STOCK_SOURCES`, which the annual monitoring indicator uses: that list carries
 * `not_applicable`, which is a real answer to "what was the stock source for this plot" and no
 * answer at all to "where did these 450 saplings come from".
 */
export const SAPLING_SOURCES: Option[] = [
  { id: 'own_nursery', label: 'Our own nursery' }, { id: 'external_nursery', label: 'External nursery' },
  { id: 'forest_department', label: 'Forest department' }, { id: 'natural_regeneration', label: 'Natural regeneration' },
  { id: 'coppice', label: 'Coppice' }, { id: 'other', label: 'Other' },
];

/** What form the planting stock was in. */
export const PLANTING_STOCK_TYPES: Option[] = [
  { id: 'seedling', label: 'Seedling' }, { id: 'clonal', label: 'Clonal' }, { id: 'grafted', label: 'Grafted' },
  { id: 'cutting', label: 'Cutting' }, { id: 'stump', label: 'Stump' }, { id: 'other', label: 'Other' },
];

/** Why saplings did not survive. A loss needs one of these; the database refuses a loss without. */
export const SAPLING_LOSS_CAUSES: Option[] = [
  { id: 'drought', label: 'Drought / no water' }, { id: 'waterlogging', label: 'Waterlogging / flood' },
  { id: 'animal_damage', label: 'Animal / cattle damage' }, { id: 'pest_disease', label: 'Pest or disease' },
  { id: 'fire', label: 'Fire' }, { id: 'poor_stock', label: 'Poor planting stock' },
  { id: 'not_planted', label: 'Never planted' }, { id: 'theft', label: 'Theft' }, { id: 'other', label: 'Other' },
];

export const STOCK_SOURCES: Option[] = [
  { id: 'own_nursery', label: 'Own nursery' }, { id: 'external_nursery', label: 'External nursery' }, { id: 'forest_department', label: 'Forest department' },
  { id: 'natural_regeneration', label: 'Natural regeneration' }, { id: 'coppice', label: 'Coppice' }, { id: 'not_applicable', label: 'Not applicable' }, { id: 'other', label: 'Other' },
];
export const SCREENING_RESULTS: Option[] = [
  { id: 'no_overlap', label: 'No overlap' }, { id: 'within_buffer', label: 'Within a buffer zone' },
  { id: 'overlaps_protected_area', label: 'Overlaps a protected area' }, { id: 'overlaps_hcv_landscape', label: 'Overlaps an HCV landscape' },
  { id: 'not_screened', label: 'Not screened' },
];
export const MONITORING_STATUS: Option[] = [{ id: 'draft', label: 'Draft' }, { id: 'verified', label: 'Verified' }];

/** The annual monitoring indicators, in the owner's order, each with its FSC criteria. */
export const MONITORING_INDICATORS: Array<{ key: string; label: string; refs: string; options: Option[]; adverse?: boolean }> = [
  { key: 'illegal_activity', label: 'Illegal or unauthorized activity reported', refs: '1.4.1 / 1.4.4', options: YES_NO, adverse: true },
  { key: 'encroachment', label: 'Encroachment on plot boundary observed', refs: '1.4.1 / 1.4.2', options: YES_NO, adverse: true },
  { key: 'fire_incident', label: 'Fire incident in the period', refs: '1.4.1', options: YES_NO, adverse: true },
  { key: 'child_forced_labour', label: 'Child or forced labour observed', refs: '2.1.1 / 2.1.2', options: YES_NO, adverse: true },
  { key: 'accident_injury', label: 'Accident or injury reported', refs: '2.3.6', options: YES_NO },
  { key: 'grievance_received', label: 'Grievance received from member or worker', refs: '2.1.4 / 4.4.1', options: YES_NO },
  { key: 'cultural_site_present', label: 'Cultural, religious or special site present', refs: '4.7.1 / 4.7.4', options: YES_NO },
  { key: 'hcv_identified', label: 'High Conservation Value identified', refs: '9.3.1 / 9.4.1', options: YES_NO },
  { key: 'rte_species_observed', label: 'RTE species observed', refs: '6.5.1 / 9.4.4', options: YES_NO },
  { key: 'natural_vegetation_converted', label: 'Natural vegetation converted to plantation', refs: '6.2.1 / 9.4.4', options: YES_NO, adverse: true },
  { key: 'native_vegetation_retained', label: 'Native vegetation retained on bunds / watercourse', refs: '6.2.2', options: YES_NO },
  { key: 'soil_water_adverse_impact', label: 'Soil or water adverse impact observed', refs: '6.7.1 / 10.10.1', options: YES_NO, adverse: true },
  { key: 'indigenous_customary_claim', label: 'Indigenous or customary claim recorded', refs: '3.3.1 / 3.6.1', options: YES_NO },
  { key: 'chemical_pesticide_applied', label: 'Chemical pesticide applied (FSC-POL-30-001)', refs: '10.7.1 / 10.7.2', options: YES_NO },
  { key: 'biopesticide_applied', label: 'Biopesticide applied, if any (neem-based)', refs: '10.7.1 / 10.7.3', options: YES_NO_NA },
  { key: 'biopesticide_screened', label: 'Biopesticide screened against FSC-POL-30-001a', refs: '10.7.2', options: YES_NO_NA },
  { key: 'gmo_planting_material', label: 'Genetically modified planting material used', refs: '10.4.1', options: YES_NO, adverse: true },
  { key: 'planting_stock_source', label: 'Planting stock source', refs: '10.4.1', options: STOCK_SOURCES },
  { key: 'intercropping_establishment', label: 'Intercropping practised in establishment phase', refs: '5.1.1 / 5.1.2', options: YES_NO_NA },
  { key: 'growth_survival_monitored', label: 'Growth and survival monitoring done', refs: '7.3.1 / 8.2', options: YES_NO },
  { key: 'harvest_in_period', label: 'Harvest carried out in the period', refs: '5.2 / 7.3.1', options: YES_NO },
  { key: 'ppe_verified_at_harvest', label: 'PPE issued and use verified at harvest', refs: '2.3.4 / 2.3.5', options: YES_NO_NH },
  { key: 'hcv_screening_applied', label: 'Enrolment HCV screening question set applied', refs: '9.3.1 / 9.4.1', options: YES_NO },
  { key: 'landscape_screening_result', label: 'Landscape / protected area screening result', refs: '9.3.1 / 9.4.2', options: SCREENING_RESULTS },
  { key: 'plot_coordinate_verified', label: 'Plot coordinate verified for screening', refs: '9.3.1', options: YES_NO },
  { key: 'rte_screening_checklist', label: 'RTE species screening checklist applied', refs: '6.5.1 / 9.4.4', options: YES_NO },
  { key: 'consultation_trigger_raised', label: 'Consultation trigger raised', refs: '4.7.1 / 9.3.5', options: YES_NO },
  { key: 'worker_age_id_verified', label: 'Worker age and identity verification at harvest', refs: '2.1.1 / 2.1.2', options: YES_NO_NH },
  { key: 'nursery_non_gmo_declaration', label: 'Nursery non-GMO declaration held', refs: '10.4.1', options: YES_NO_NA },
];

export function financialYear(date = new Date()): { label: string; from: string; to: string } {
  const y = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return { label: `${y}-${String((y + 1) % 100).padStart(2, '0')}`, from: `${y}-04-01`, to: `${y + 1}-03-31` };
}

// Work sites (0086) - farms, harvest sites and processing units share one master.
export const SITE_TYPES: Option[] = [
  { id: 'farm', label: 'Farm' }, { id: 'harvest_site', label: 'Harvest site' }, { id: 'nursery', label: 'Nursery' },
  { id: 'depot', label: 'Depot / log yard' }, { id: 'sawmill', label: 'Sawmill' }, { id: 'seasoning_unit', label: 'Seasoning unit / kiln' },
  { id: 'treatment_plant', label: 'Treatment plant' }, { id: 'processing_unit', label: 'Other processing unit' },
  { id: 'warehouse', label: 'Warehouse' }, { id: 'office', label: 'Office' }, { id: 'other', label: 'Other' },
];
export const SITE_OWNERSHIP: Option[] = [
  { id: 'own', label: 'Our own' }, { id: 'leased', label: 'Leased by us' }, { id: 'contracted', label: 'Run by a contractor' },
  { id: 'third_party', label: 'Third party (supplier / processor)' },
];
export const SITE_STATUS: Option[] = [{ id: 'active', label: 'Active' }, { id: 'inactive', label: 'Inactive' }, { id: 'closed', label: 'Closed' }];

// Renewals (0087)
export const DUE_STATES: Option[] = [
  { id: 'expired', label: 'Expired' }, { id: 'due_30', label: 'Due in 30 days' }, { id: 'due_90', label: 'Due in 90 days' }, { id: 'later', label: 'Later' },
];

/**
 * The thirteen roles a party can hold in the chain (party_roles_role_type_check, less the
 * platform's own). One list, used by KYC requirements and by engagements, so a role is named the
 * same way wherever it appears.
 */
export const PARTY_ROLES: Option[] = [
  { id: 'farmer', label: 'Farmer' }, { id: 'nursery', label: 'Nursery' }, { id: 'supplier', label: 'Supplier' },
  { id: 'service_provider', label: 'Service provider' }, { id: 'contractor', label: 'Contractor' },
  { id: 'transporter', label: 'Transporter' }, { id: 'aggregator', label: 'Aggregator' }, { id: 'processor', label: 'Processor' },
  { id: 'exporter', label: 'Exporter' }, { id: 'buyer', label: 'Buyer' }, { id: 'auditor', label: 'Auditor' },
  { id: 'staff', label: 'Staff' }, { id: 'representative', label: 'Representative' },
];
